/**
 * Phase 8 · C-12 — Billing account read service.
 *
 * Surface for `useBillingBalance()`. Reads ggj_b2b_billing_accounts (migration
 * 098) and derives display-ready values: days remaining on trial and whether
 * the trial is currently active.
 *
 * Read-only for this ticket — no "adjust balance" mutations.
 */
import { supabase } from '@core/supabase/client';

export interface BillingBalance {
  /** Remaining AI credits. 0 when out. */
  balance: number;
  /** Whole days remaining on the trial; 0 when ended / no trial. */
  daysLeft: number;
  /** True when trial_ends_at is set and still in the future. */
  isTrialActive: boolean;
  /** Plan slug, e.g. "trial", "growth". Defaults to "trial" when unset. */
  planSlug: string;
}

const DAY_MS = 24 * 60 * 60 * 1000;

function computeDaysLeft(trialEndsAt: string | null | undefined): number {
  if (!trialEndsAt) return 0;
  const t = Date.parse(trialEndsAt);
  if (!Number.isFinite(t)) return 0;
  const diff = t - Date.now();
  if (diff <= 0) return 0;
  return Math.ceil(diff / DAY_MS);
}

function computeIsTrialActive(trialEndsAt: string | null | undefined): boolean {
  if (!trialEndsAt) return false;
  const t = Date.parse(trialEndsAt);
  return Number.isFinite(t) && t > Date.now();
}

export const billingService = {
  /**
   * Fetch the billing snapshot for an org. Throws on RLS / network errors so
   * TanStack Query can surface `isError` to the UI.
   *
   * When the row does not exist yet (e.g. a freshly-seeded tenant), returns a
   * safe zero-state that reads as "no trial, no credits, plan=trial".
   */
  async getBalance(orgId: string): Promise<BillingBalance> {
    const { data, error } = await supabase
      .from('ggj_b2b_billing_accounts')
      .select('balance, trial_ends_at, plan_slug')
      .eq('org_id', orgId)
      .maybeSingle();

    if (error) throw new Error(error.message);

    if (!data) {
      return { balance: 0, daysLeft: 0, isTrialActive: false, planSlug: 'trial' };
    }

    const row = data as {
      balance: number | null;
      trial_ends_at: string | null;
      plan_slug: string | null;
    };

    return {
      balance: Math.max(0, row.balance ?? 0),
      daysLeft: computeDaysLeft(row.trial_ends_at),
      isTrialActive: computeIsTrialActive(row.trial_ends_at),
      planSlug: row.plan_slug ?? 'trial',
    };
  },
};
